<template>
    <node-view-wrapper>
        <div class="custom-image-tiptap relative" :style="`width:${width}px;height:${height}px;`">
            <div
                draggable="true"
                data-drag-handle
                class="flex items-center justify-center absolute w-7 h-7 -ml-2 -mt-2 z-10 bg-white rounded-lg shadow border cursor-move | hover:text-gray-400 hover:bg-gray-100 | transition duration-200"
                v-if="isLoaded"
            >
                <div class="drag-handle w-3.5 h-3.5" />
            </div>
            <img
                :src="node.attrs.src"
                :width="`${width}px`"
                :height="`${height}px`"
                alt="Email uploaded illustration"
                @load="onLoad"
                @error="OnError"
                v-if="!isLoaded"
            />
            <vue-draggable-resizable
                :w="width"
                :h="height"
                :x="0"
                :y="0"
                :active="isActive"
                :draggable="false"
                :resizable="true"
                :handles="['br']"
                :max-width="maxWidth"
                :lock-aspect-ratio="true"
                class-name-active="ring-2 rounded-sm ring-blue-500 shadow-xl border-blue-600"
                @activated="isActive = true"
                @deactivated="isActive = false"
                @resizing="onResize"
                v-else
            >
                <img
                    :src="node.attrs.src"
                    :width="`${width}px`"
                    :height="`${height}px`"
                    alt="Email uploaded illustration"
                />
            </vue-draggable-resizable>
        </div>
    </node-view-wrapper>
</template>

<script>
    import { NodeViewWrapper } from '@tiptap/vue-3';
    import VueDraggableResizable from 'vue-draggable-resizable';
    import 'vue-draggable-resizable/style.css';

    const Vue3NodeViewWrapper = {
        extends: NodeViewWrapper,
        compatConfig: {
            MODE: 3,
            RENDER_FUNCTION: false,
            INSTANCE_SCOPED_SLOTS: false,
        },
    };

    export default {
        components: {
            NodeViewWrapper: Vue3NodeViewWrapper,
            VueDraggableResizable,
        },

        props: {
            /**
             * Injected Node Object from the @tiptap/core by the VueNodeViewRenderer.
             * @see https://tiptap.dev/docs/editor/extensions/custom-extensions/create-new/node#node-options
             */
            node: {
                type: Object,
            },
            updateAttributes: {
                type: Function,
            },
        },

        data() {
            return {
                isLoaded: false,
                isActive: false,
                maxWidth: 1000,
            };
        },

        computed: {
            src: {
                get() {
                    return this.node.attrs.src;
                },
                set(src) {
                    this.updateAttributes({ src });
                },
            },

            width() {
                const parsedWidth = Number.parseInt(this.node.attrs.width, 10);

                if (this.isLoaded) {
                    return Number.isFinite(parsedWidth) ? parsedWidth : 100;
                }

                return 100;
            },

            height() {
                const parsedHeight = Number.parseInt(this.node.attrs.height, 10);

                if (this.isLoaded) {
                    return Number.isFinite(parsedHeight) ? parsedHeight : 100;
                }

                return 100;
            },
        },

        methods: {
            onLoad(event) {
                const img = event.target;
                const naturalWidth = img.naturalWidth;
                const naturalHeight = img.naturalHeight;
                const attrWidth = Number.parseInt(this.node.attrs.width, 10);
                const attrHeight = Number.parseInt(this.node.attrs.height, 10);
                const hasWidth = Number.isFinite(attrWidth);
                const hasHeight = Number.isFinite(attrHeight);

                let width = naturalWidth;
                let height = naturalHeight;

                if (naturalWidth > 0 && naturalHeight > 0) {
                    const naturalRatio = naturalWidth / naturalHeight;

                    if (hasWidth && hasHeight) {
                        const attrRatio = attrWidth / attrHeight;

                        // Keep explicit user-resized dimensions only when they preserve the image ratio.
                        if (Math.abs(naturalRatio - attrRatio) < 0.01) {
                            width = attrWidth;
                            height = attrHeight;
                        }
                    } else if (hasWidth) {
                        width = attrWidth;
                        height = width / naturalRatio;
                    } else if (hasHeight) {
                        height = attrHeight;
                        width = height * naturalRatio;
                    }
                }

                if (width > this.maxWidth) {
                    height = (height / width) * this.maxWidth;
                    width = this.maxWidth;
                }

                this.updateAttributes({
                    width: Math.round(width),
                    height: Math.round(height),
                });
                this.isLoaded = true;
            },

            OnError() {
                this.isLoaded = false;
            },

            onResize(left, top, width, height) {
                this.updateAttributes({ width, height });
            },
        },
    };
</script>

<style lang="less">
    .drag-handle {
        background-image: url('data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 16"><path fill-opacity="0.2" d="M4 14c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zM2 6C.9 6 0 6.9 0 8s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6C.9 0 0 .9 0 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" /></svg>');
        background-repeat: no-repeat;
        background-size: contain;
        background-position: center;
    }

    .custom-image-tiptap {
        .vdr {
            @apply border-none;
        }

        .resizable {
            .handle {
                @apply w-6 h-6 bg-white rounded-full border border-gray-400 !important;
            }
        }
    }
</style>
